import {
  AccountOwnerIcon,
  BankIcon,
  CakeIcon,
  CardIcon,
  IDIcon,
  LogoSquare,
  PersonIcon,
  PlayIcon,
  RightArrowIcon,
} from 'components/icons';
import { AppRegistrationSection } from '../../AppRegistrationSection';
import {
  Button,
  ButtonActor,
  FormWarning,
  Heading,
  Link,
  LinkActor,
  Modal,
} from 'components';
import {
  EMBEDDED_IN_WEBVIEW_PARAM,
  LOGIN_ROUTE,
  MOBILE_APP_LOGIN,
  MOBILE_SOURCE_PARAM,
  SESSION_MOBILE_INSTANCE_ID,
} from 'utils/constants';
import { Frame } from 'components/organisms';
import { frameConfig } from '../../AppConfig';
import { getScreenTranslation } from '../../utils/translations';
import { routes } from 'routes';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useProduct } from 'hooks/useProduct';
import { useTranslation } from 'react-i18next';
import Card from 'components/atoms/Card/Card';
import classes from 'classnames';
import layout from 'scss/layout.module.scss';
import React, { useCallback, useEffect, useState } from 'react';
import Requirements, {
  Requirement,
} from 'components/molecules/Requirements/Requirements';
import Spacer from 'components/atoms/Spacer/Spacer';
import styles from './Introduction.module.scss';
import Title from 'components/atoms/Title/Title';

const banks = [
  'Banco Cooperativo',
  'Banco Sabadell',
  'Banco Santander',
  'Bankinter',
  'CRC - Caja Rural Central',
  'Caixa Popular',
  'Caixa Rural Les Coves',
  'Caixa Vinaros',
  'CaixaBank',
  'Caja Rural',
  'Deutsche Bank',
  'EVO Banco',
  'Global Caja',
  'ING',
  'IberCaja',
  'Caja Viva',
  'CajaSiete',
  'Cajamar',
  'Cajasur',
  'Imagin',
  'Kutxabank',
  'Laboral Kutxa',
  'Openbank',
  'RuralNostra',
  'Ruralvia',
  'Unicaja',
];

export default function Introduction() {
  const navigate = useNavigate();
  const [isBankModalOpen, setIsBankModalOpen] = useState(false);
  const [isBankCardModalOpen, setIsBankCardModalOpen] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [inWebView, setInWebView] = useState(false);

  const [searchParams] = useSearchParams();
  const { valueRelatedToProduct } = useProduct();

  const i18Next = useTranslation('introduction_list_sections');
  const translations = getScreenTranslation('introduction', i18Next);

  useEffect(() => {
    if (searchParams.get(EMBEDDED_IN_WEBVIEW_PARAM) === 'true') {
      sessionStorage.setItem(EMBEDDED_IN_WEBVIEW_PARAM, 'true');
      setInWebView(true);
    }
    const source = searchParams.get(MOBILE_SOURCE_PARAM);
    if (source) {
      sessionStorage.setItem(MOBILE_SOURCE_PARAM, source);
    }
  }, []);

  useEffect(() => {
    const applicationInstanceId = searchParams.get(SESSION_MOBILE_INSTANCE_ID);
    if (applicationInstanceId)
      sessionStorage.setItem(SESSION_MOBILE_INSTANCE_ID, applicationInstanceId);
  }, []);

  const toggleBankModal = useCallback(() => {
    const nextIsBankModalOpen = !isBankModalOpen;
    if (!isVideoOpen || nextIsBankModalOpen !== isVideoOpen)
      setIsBankModalOpen(nextIsBankModalOpen);
  }, [isVideoOpen, isBankModalOpen]);

  const toggleBankCardModal = useCallback(() => {
    setIsBankCardModalOpen((prevState) => !prevState);
  }, []);

  const toggleVideoModal = useCallback(() => {
    // hide bank modal temporally to show the video modal
    if (isVideoOpen) setIsBankModalOpen(true);
    else setIsBankModalOpen(false);

    setIsVideoOpen((prevState) => !prevState);
  }, [isVideoOpen]);

  const listSection: string[] = [
    i18Next.t('point1'),
    i18Next.t('point2'),
    i18Next.t('point3'),
    i18Next.t('point4'),
  ];

  // @ts-ignore - TODO: enable string[] values
  const content = {
    ...translations,
    modalBankBody: {
      ...translations.modalBankBody,
      banks,
    },
    modalBankCard: {
      ...translations.modalBankCard,
      listSection,
    },
  };

  const requirements = [
    {
      icon: <IDIcon data-test="introduction-icon-id" />,
      text: content.idText,
      dataTest: 'xpath-Requeriments-LiveIn-Spain',
    },
    {
      icon: <CakeIcon data-test="introduction-icon-cake" />,
      text: content.adultText,
      dataTest: 'xpath-Requeriments-Age',
    },
    {
      icon: <AccountOwnerIcon data-test="introduction-icon-account-owner" />,
      text: content.ownerAccountText,
      dataTest: 'xpath-Requeriments-owner',
    },
    {
      icon: <BankIcon data-test="introduction-icon-bank" />,
      additionalTextElement: () => (
        <div className={styles['bank-info-link-wrapper']}>
          <Link
            className={styles['bank-info-link']}
            dataTest="introduction-bank-info-link"
            onClick={() => toggleBankModal()}
          >
            {content.bankTextMoreInfo}
          </Link>
        </div>
      ),
      text: content.bankIdentification,
      dataTest: 'xpath-Requeriments-BankAccount',
    },
  ] as Array<Requirement>;

  const bankInfo = [
    {
      icon: <CardIcon data-test="icon-card" />,
      text: content.bankText,
      textHandler: toggleBankCardModal,
    },
  ] as Array<Requirement>;

  return (
    <>
      <Modal
        isOpen={isVideoOpen}
        className={styles['modal-video-wrapper']}
        dataTest="modal-video"
        onClose={toggleVideoModal}
        onCloseOutside={toggleVideoModal}
        onClickOutside={toggleVideoModal}
        isMobileFullWidth
      >
        <iframe
          src="https://www.youtube.com/embed/0mdcVWmr0HQ"
          className={styles['modal-video']}
          title={content.youtubeVideoPlayer}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture;  web-share"
          allowFullScreen
        ></iframe>
      </Modal>
      <Modal
        dataTest="modal-bank"
        isOpen={isBankModalOpen}
        classNameWrapper={styles['modal-wrapper']}
        className={styles.modal}
        onClose={toggleBankModal}
        onClickOutside={toggleBankModal}
        isMobileFullWidth
      >
        <div className={styles['modal-content']}>
          <div className={styles['modal-header-wrapper']}>
            <BankIcon data-test="icon-bank" />
            <Heading
              className={styles['modal-header']}
              dataTest="introduction-heading"
              text={content.modalBankHeading}
            />
          </div>
          <Card
            className={styles['modal-body-card']}
            dataTest="introduction-card-body"
          >
            <div className={styles['modal-body-card-row']}>
              <div className={styles['modal-body-card-row-number-circle']}>
                <div
                  className={styles['modal-body-card-row-number']}
                  data-test="introduction-card-body-circle-1"
                >
                  1
                </div>
              </div>
              <div
                className={styles['modal-body-card-row-text']}
                data-test="introduction-card-body-circle-text-1"
              >
                {content.modalBankBody.point1}
              </div>
            </div>
            <div className={styles['modal-body-card-row']}>
              <div className={styles['modal-body-card-row-number-circle']}>
                <div
                  className={styles['modal-body-card-row-number']}
                  data-test="introduction-card-body-circle-2"
                >
                  2
                </div>
              </div>
              <div
                className={styles['modal-body-card-row-text']}
                data-test="introduction-card-body-circle-text-2"
              >
                {content.modalBankBody.point2}
              </div>
            </div>
            <div className={styles['modal-body-card-row']}>
              <div className={styles['modal-body-card-row-number-circle']}>
                <div
                  className={styles['modal-body-card-row-number']}
                  data-test="introduction-card-body-circle-3"
                >
                  3
                </div>
              </div>
              <div
                className={styles['modal-body-card-row-text']}
                data-test="introduction-card-body-circle-text-3"
              >
                {content.modalBankBody.point3}
              </div>
            </div>
            <div className={styles['modal-separator']} />
            <Link
              className={styles['modal-video-link-wrapper']}
              dataTest="introduction-video-link"
              onClick={toggleVideoModal}
            >
              <PlayIcon
                className={styles['modal-video-link-icon']}
                data-test="introduction-video-play-icon"
              />
              {content.modalBankBody.video}
            </Link>
          </Card>
          <div
            className={styles['modal-body-header']}
            data-test="introduction-header-2"
          >
            {content.modalBankBody.header2}
          </div>
          <Card
            className={styles['modal-banks-card']}
            dataTest="introduction-modal-bank-card"
          >
            {content.modalBankBody.banks.map((bank: string) => (
              <div key={bank}>{bank}</div>
            ))}
          </Card>
          <div className={styles['modal-agreed-btn-wrapper']}>
            <Button
              className={styles['modal-agreed-btn']}
              dataTest="introduction-modal-agreed-btn"
              onClick={toggleBankModal}
            >
              {content.modalBankBody.agreedButton}
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        dataTest="introduction-modal-bank"
        isOpen={isBankCardModalOpen}
        className={styles.modal}
        onClose={toggleBankCardModal}
        isMobileFullWidth
      >
        <div className={styles['modal-content']}>
          <div className={styles['modal-header-wrapper']}>
            <CardIcon
              className={styles['modal-header-icon']}
              data-test="introduction-modal-bank-card-icon"
            />
            <Heading
              className={styles['modal-header']}
              dataTest="introduction-modal-bank-heading"
              text={content.modalBankCard.title}
            />
          </div>
          <div
            className={styles['modal-steps-wrapper']}
            data-test="introduction-modal-bank-wrapper"
            dangerouslySetInnerHTML={{
              __html: content.modalBankCard.description,
            }}
          />
          <div className={styles['modal-bank-layout']}>
            <div
              className={styles['modal-bank-text']}
              data-test="introduction-modal-bank-title"
            >
              {content.modalBankCard.listTitle}
            </div>
            <div
              className={classes(
                [styles['modal-banks-wrapper']],
                styles['modal-card-banks-wrapper'],
              )}
            >
              {content.modalBankCard.listSection.map(
                (point: string, index: number) => (
                  <div
                    className={styles.section}
                    data-test={`introduction-modal-bank-section-${index}`}
                    key={`bank-section-${index}`}
                  >
                    <div
                      className={styles['bullet-point']}
                      data-test={`introduction-modal-bank-section-${index}-point`}
                    ></div>
                    <span
                      className={styles['modal-bank']}
                      data-test={`introduction-modal-bank-section-${index}-text`}
                    >
                      {point}
                    </span>
                  </div>
                ),
              )}
            </div>
          </div>
          <Button
            className={styles['modal-agree-btn']}
            dataTest="introduction-modal-bank-agree-btn"
            onClick={toggleBankCardModal}
          >
            {content.modalAgreeBtn}
          </Button>
        </div>
      </Modal>
      <Frame
        {...frameConfig}
        hideLogout
        sectionID={AppRegistrationSection.Intro}
        bodyWrapperClassName={classes(
          layout['width-lg'],
          layout['frame-x-centered'],
        )}
        inWebView={inWebView}
      >
        <Title
          text={content.title}
          className={styles.title}
          dataTest="css-Request-FirstLoan-Title"
        />

        <Spacer
          marginBottom={{ default: '40px', xs: '20px' }}
          className={styles['login-section']}
        >
          <Title
            text={content.sessionTitle}
            className={styles.subheading}
            dataTest="xpath-AreYou-Client"
          />
          <LinkActor
            linkProps={{
              href: inWebView ? MOBILE_APP_LOGIN : LOGIN_ROUTE,
              leftIcon: <PersonIcon />,
              rightIcon: <RightArrowIcon />,
              className: styles['login-button'],
              dataTest: 'css-Init-Session',
            }}
          >
            {content.sessionLink}
          </LinkActor>
        </Spacer>

        <Title
          text={content.requestTitle}
          className={classes(styles.subheading, styles['text-align-left'])}
          dataTest="xpath-Requeriments-Title"
        />
        <Requirements
          container={requirements}
          cardClassName={styles['requirement-card-one']}
          dataTest="xpath-Requeriments-Container"
        />
        <Title
          text={content.speedupTitle}
          className={classes(styles.subheading, styles['text-align-left'])}
          dataTest="xpath-Speed-Your-Request-Title"
        />
        <Requirements
          container={bankInfo}
          cardClassName={styles['requirement-card-two']}
          dataTest="xpath-Speed-Your-Request-Container"
        />

        <ButtonActor
          buttonProps={{
            onClick: () => navigate(routes.FirstLoan_Email),
            fill: true,
            dataTest: 'css-Start-Button',
            className: styles.button,
          }}
        >
          {content.continueButtonText}
        </ButtonActor>
        {Boolean(valueRelatedToProduct) && (
          <FormWarning
            dataTest="introduction-warning-already-registered"
            intent="info"
            icon={<LogoSquare />}
            text={content.userDescription}
            title={content.userTitle}
            linkHref={LOGIN_ROUTE}
            linkText={content.userLink}
            className={styles['inform-form']}
            headingClassName={styles['inform-form-heading']}
            textContentClassName={styles['inform-form-text-content']}
          />
        )}
      </Frame>
    </>
  );
}
